import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Button } from "@/components/ui/button";
import { UpdatableOrderStatus } from "../../api/updateProductStoreOrderStatus/types";
import { ORDER_STATUS_STYLES } from "../../utils/orderStatusStyles";

interface UpdateOrderStatusConfirmationProps {
  isOpen: boolean;
  confirmationNumber: string;
  targetStatus: UpdatableOrderStatus | null;
  loading: boolean;
  onConfirm: (sendNotification: boolean) => void;
  onCancel: () => void;
}

const UpdateOrderStatusConfirmation: React.FC<
  UpdateOrderStatusConfirmationProps
> = ({
  isOpen,
  confirmationNumber,
  targetStatus,
  loading,
  onConfirm,
  onCancel,
}) => {
  const style = targetStatus ? ORDER_STATUS_STYLES[targetStatus] : null;
  // Only the "shipped" transition has a customer notification template.
  const canNotify = targetStatus === "shipped";
  const [sendNotification, setSendNotification] = useState(true);

  // Reset to the default (checked) each time the dialog opens.
  useEffect(() => {
    if (isOpen) {
      setSendNotification(true);
    }
  }, [isOpen]);

  return (
    <Transition as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-40" onClose={onCancel}>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="scale-95"
              enterTo="scale-100"
              leave="transform transition ease-in duration-200"
              leaveFrom="scale-100"
              leaveTo="scale-95"
            >
              <DialogPanel className="w-full max-w-md transform rounded-lg bg-white dark:bg-slate-800 p-6 text-left shadow-xl transition-all">
                <DialogTitle className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Update order status
                </DialogTitle>
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    Are you sure you want to mark order{" "}
                    <span className="font-mono font-semibold">
                      {confirmationNumber}
                    </span>{" "}
                    as{" "}
                    <span className="font-semibold">
                      {style?.label.toLowerCase() ?? targetStatus}
                    </span>
                    ?
                  </p>
                </div>

                {canNotify && (
                  <label className="mt-4 flex cursor-pointer items-center gap-2">
                    <input
                      checked={sendNotification}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={loading}
                      onChange={(e) => setSendNotification(e.target.checked)}
                      type="checkbox"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Send Notification
                    </span>
                  </label>
                )}

                <div className="mt-5 flex justify-end space-x-3">
                  <Button
                    disabled={loading}
                    onClick={onCancel}
                    type="button"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={loading || !style}
                    onClick={() => onConfirm(canNotify && sendNotification)}
                    type="button"
                  >
                    {loading ? "Updating…" : `Mark as ${style?.label ?? ""}`}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UpdateOrderStatusConfirmation;
